import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Images from '../../Images';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";



const CategoryCard = ({ category, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative cursor-pointer group"
    >
      <div className={`rounded-2xl bg-gradient-to-br ${category.gradient} p-6 h-[200px] transform transition-all duration-300 hover:scale-105 hover:shadow-xl`}>
        <div className="flex relative z-10 flex-col justify-center items-center h-full text-center">
          {/* Centered Image */}
          <motion.div
            animate={{
              y: [-5, 5, -5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="mb-4"
          >
            <img 
              src={category.icon} 
              alt={category.title}
              className="object-contain w-16 h-16"
            />
          </motion.div>

          {/* Centered Text */}
          <div className="space-y-2">
            <h3 className="w-full font-bold text-white text-md">{category.title}</h3>
            <div className="flex justify-center items-center space-x-2">
              <span className="text-sm text-white/80">Subscribers:</span>
              <motion.span 
                className="font-bold text-white"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.2 + 0.5 }}
              >
                {category.count}
              </motion.span>
            </div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="overflow-hidden absolute inset-0 rounded-2xl opacity-10 bg-pattern">
          <svg width="100%" height="100%">
            <pattern id="pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="2" fill="white"/>
            </pattern>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern)"/>
          </svg>
        </div>
      </div>
    </motion.div>
  );
};

const CategoryCarousel = ({categoryData}) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    cssEase: "linear",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const categories = [
    {
      id: 1,
      title: "Fashion & Accessories",
      count: "3445",
      color: "#1a75ff",
      icon: Images.fashion,
      gradient: "from-blue-500 to-blue-600"
    },
    {
      id: 2,
      title: "Electronics & Gadgets",
      count: "3445",
      color: "#5c4d1a",
      icon: Images.electronics,
      gradient: "from-amber-700 to-amber-800"
    },
    {
      id: 3,
      title: "Events & Entertainment",
      count: "3445",
      color: "#ab1768",
      icon: Images.cinema,
      gradient: "from-pink-600 to-pink-700"
    },
    {
      id: 4,
      title: "Baby & Mother Care",
      count: "3445",
      color: "#e6194b",
      icon: Images.mother_and_daughter,
      gradient: "from-red-500 to-red-600"
    },
    {
      id: 5,
      title: "Sports",
      count: "345",
      color: "#FF0000",
      icon: Images.sports,
      gradient: "from-red-600 to-red-700"
    },
    {
      id: 6,
      title: "Real Estate & Housing",
      count: "345",
      color: "#0FF140",
      icon: Images.house,
      gradient: "from-green-500 to-green-600"
    },
    {
      id: 7,
      title: "Beauty, Spa & Wellness",
      count: "345",
      color: "#06655D",
      icon: Images.spa,
      gradient: "from-teal-600 to-teal-700"
    },
    {
      id: 8,
      title: "Restaurants & Eateries",
      count: "345",
      color: "#4E217F",
      icon: Images.restaurant,
      gradient: "from-purple-600 to-purple-700"
    },
    {
      id: 9,
      title: "Travel & Tourism",
      count: "345",
      color: "#70D739",
      icon: Images.travel,
      gradient: "from-lime-500 to-lime-600"
    },
    {
      id: 10,
      title: "Groceries",
      count: "345",
      color: "#9D2235",
      icon: Images.grocery,
      gradient: "from-rose-600 to-rose-700"
    },
    {
      id: 11,
      title: "Automotive",
      count: "345",
      color: "#2124BA",
      icon: Images.car,
      gradient: "from-indigo-600 to-indigo-700"
    },
    {
      id: 12,
      title: "Furniture",
      count: "345",
      color: "#EC7202",
      icon: Images.furniture,
      gradient: "from-orange-500 to-orange-600"
    },
  ];

  const updatedCategories = categories.map(category => {
    const matchedItem = categoryData.find(
      item => item.subscribed_item.toLowerCase() === category.title.toLowerCase()
    );
    return {
      ...category,
      count: matchedItem ? matchedItem.occurance.toString() : category.count
    };
  });

  return (
    <div className="px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-7xl"
      >
        <h2 className="mb-8 text-2xl font-bold text-gray-800">Browse Categories</h2>
        <Slider {...settings}>
          {updatedCategories.map((category, index) => (
            <div key={category.id} className="px-3">
              <CategoryCard category={category} index={index} />
            </div>
          ))}
        </Slider>
      </motion.div>
    </div>
  );
};

export default CategoryCarousel;
